import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseHttpService } from './base-http.service';
import {
  MonthlySummaryResponse,
  MetricSummary,
  YearlySummaryResponse,
  YearlyMetricSummary
} from '../interfaces/statistics.interface';

/**
 * Statisztikai aggregáció service.
 *
 * DB szintű összesítéseket hív a /stats-summary endpointokon keresztül.
 * Ezek a PostgreSQL függvényeket használják — a számítás nem a frontenden történik.
 */
@Injectable({
  providedIn: 'root'
})
export class StatsAggregationService extends BaseHttpService {

  constructor(
    http: HttpClient,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    super(http, platformId);
  }

  // ===========================================================================
  // CC havi összesítők
  // ===========================================================================

  /**
   * Egy felhasználó CC havi összesítése.
   * @returns 3 metrika: initiated_calls, appointment_scheduled, property_insurance
   */
  getCCMonthlyUserSummary(userId: number, month: number, year: number): Observable<MetricSummary[]> {
    return this.get<MonthlySummaryResponse>(
      `/stats-summary/cc/monthly/${userId}?month=${month}&year=${year}`
    ).pipe(map(res => res.metrics));
  }

  /**
   * Összes CC kolléga havi összesítése (Összesen sor).
   */
  getCCMonthlyAllSummary(month: number, year: number): Observable<MetricSummary[]> {
    return this.get<MonthlySummaryResponse>(
      `/stats-summary/cc/monthly-all?month=${month}&year=${year}`
    ).pipe(map(res => res.metrics));
  }

  // ===========================================================================
  // KÜM havi összesítők
  // ===========================================================================

  /**
   * Egy felhasználó KÜM havi összesítése.
   * @returns 5 metrika: appointment_scheduled, successful_first_meetings, stb.
   */
  getKumMonthlyUserSummary(userId: number, month: number, year: number): Observable<MetricSummary[]> {
    return this.get<MonthlySummaryResponse>(
      `/stats-summary/kum/monthly/${userId}?month=${month}&year=${year}`
    ).pipe(map(res => res.metrics));
  }

  /**
   * Összes KÜM kolléga havi összesítése (Összesen sor).
   */
  getKumMonthlyAllSummary(month: number, year: number): Observable<MetricSummary[]> {
    return this.get<MonthlySummaryResponse>(
      `/stats-summary/kum/monthly-all?month=${month}&year=${year}`
    ).pipe(map(res => res.metrics));
  }

  // ===========================================================================
  // CC éves összesítők
  // ===========================================================================

  /**
   * Egy felhasználó CC éves összesítése — 12 hónap × 3 metrika egyetlen kéréssel.
   */
  getCCYearlyUserSummary(userId: number, year: number): Observable<YearlyMetricSummary[]> {
    return this.get<YearlySummaryResponse>(
      `/stats-summary/cc/yearly/${userId}?year=${year}`
    ).pipe(map(res => res.metrics));
  }

  /**
   * Összes CC kolléga éves összesítése — 12 hónap × 3 metrika.
   */
  getCCYearlyAllSummary(year: number): Observable<YearlyMetricSummary[]> {
    return this.get<YearlySummaryResponse>(
      `/stats-summary/cc/yearly-all?year=${year}`
    ).pipe(map(res => res.metrics));
  }

  // ===========================================================================
  // KÜM éves összesítők
  // ===========================================================================

  /**
   * Egy felhasználó KÜM éves összesítése — 12 hónap × 5 metrika egyetlen kéréssel.
   */
  getKumYearlyUserSummary(userId: number, year: number): Observable<YearlyMetricSummary[]> {
    return this.get<YearlySummaryResponse>(
      `/stats-summary/kum/yearly/${userId}?year=${year}`
    ).pipe(map(res => res.metrics));
  }

  /**
   * Összes KÜM kolléga éves összesítése — 12 hónap × 5 metrika.
   */
  getKumYearlyAllSummary(year: number): Observable<YearlyMetricSummary[]> {
    return this.get<YearlySummaryResponse>(
      `/stats-summary/kum/yearly-all?year=${year}`
    ).pipe(map(res => res.metrics));
  }

  // ===========================================================================
  // XLSX export
  // ===========================================================================

  /**
   * Az összesítő oldal táblázatainak exportálása XLSX-be.
   * `month = null` esetén az éves nézet kerül a fájlba.
   */
  exportSummary(userIds: number[], year: number, month: number | null, isKumMode: boolean): Observable<Blob> {
    return this.postBlob('/stats-summary/export', {
      user_ids: userIds,
      year,
      month,
      is_kum_mode: isKumMode
    });
  }
}
